
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { WhatsAppPromoProps, FormValues } from "./whatsapp/types";
import PhoneNumberForm from "./whatsapp/PhoneNumberForm";
import SuccessView from "./whatsapp/SuccessView";
import { useConsentDetection } from "./whatsapp/useConsentDetection";
import { useConfetti } from "./whatsapp/useConfetti";

const WhatsAppPromo = ({ isOpen = false, onClose = () => {}, autoShow = false }: WhatsAppPromoProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false); // Start with dialog closed
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [userDismissed, setUserDismissed] = useState(false);
  const [hasAutoShown, setHasAutoShown] = useState(false);
  
  const { consentHandled } = useConsentDetection();
  const { triggerConfetti } = useConfetti();

  // Effect to sync with external isOpen prop
  useEffect(() => {
    if (isOpen) {
      setIsDialogOpen(isOpen);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsDialogOpen(false);
    setUserDismissed(true);
    onClose();
  };

  // Effect to auto-show popup after consent is handled
  useEffect(() => {
    if (consentHandled && !userDismissed && autoShow && !hasAutoShown) {
      const timer = setTimeout(() => {
        console.log('Auto-showing WhatsApp popup after consent handled');
        setIsDialogOpen(true);
        setHasAutoShown(true);
      }, 2000); // Increased delay to 2 seconds to ensure consent popup is fully gone

      return () => clearTimeout(timer);
    }
  }, [consentHandled, userDismissed, autoShow, hasAutoShown]);

  const onSubmit = async (values: FormValues) => {
    try {
      setIsSubmitting(true);
      const formattedNumber = `${values.countryCode}${values.phoneNumber}`;
      console.log("Submitting form data:", { 
        phone_number: formattedNumber, 
        email: values.email,
        receive_updates: values.receiveUpdates 
      });
      
      const { error } = await supabase
        .from("whatsapp_subscribers")
        .insert([{ 
          phone_number: formattedNumber,
          email: values.email,
          receive_updates: values.receiveUpdates
        }]);

      if (error) throw error;

      setIsSuccess(true);
      triggerConfetti();
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" style={{ zIndex: 9999 }}>
        {!isSuccess ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-3xl md:text-4xl font-bold text-center mb-2">
                <span style={{ color: '#0b57d0' }}>AI</span> is Coming to WhatsApp! <span role="img" aria-label="rocket">🚀</span>
              </DialogTitle>
              <DialogDescription className="text-center pt-4 space-y-4">
                <p className="text-lg text-gray-600">
                  Be among the first to experience AI on WhatsApp.
                  Join our waitlist today!
                </p>
                <div className="space-y-2 text-gray-600">
                  <p><span role="img" aria-label="sparkles">✨</span> Early access</p>
                  <p><span role="img" aria-label="gift">🎁</span> Exclusive launch offers</p>
                  <p><span role="img" aria-label="mobile">📱</span> Instant AI assistance</p>
                </div>
              </DialogDescription>
            </DialogHeader>
            <PhoneNumberForm onSubmit={onSubmit} isSubmitting={isSubmitting} />
          </>
        ) : (
          <SuccessView onClose={handleClose} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WhatsAppPromo;
